import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, KeyRound, Trash2, UserX, UserCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const inputCls = "w-full h-10 px-3 rounded-md bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none";
const mini = "h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:text-white hover:bg-white/5 transition-colors";

const RoleBadge = ({ role }) => (
  <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${role === "admin" ? "border-indigo-500/40 text-indigo-300 bg-indigo-500/10" : "border-cyan-500/30 text-cyan-300 bg-cyan-500/10"}`}>
    {role}
  </span>
);

export default function Team() {
  const { user: me } = useAuth();
  const nav = useNavigate();
  const [members, setMembers] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "member" });
  const [newPw, setNewPw] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/team").then((r) => setMembers(r.data)).catch((e) => {
    if (e.response?.status === 403) return nav("/admin");
    toast.error(formatErr(e));
  });
  useEffect(() => { load(); }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); setShowAdd(false); setResetFor(null); setForm({ email: "", name: "", password: "", role: "member" }); setNewPw(""); await load(); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const add = (e) => { e.preventDefault(); run(() => api.post("/team", form), `${form.email} added`); };
  const reset = (e) => { e.preventDefault(); run(() => api.post(`/team/${resetFor.id}/reset-password`, { password: newPw }), "Password updated"); };
  const toggleActive = (m) => run(() => api.put(`/team/${m.id}`, { active: !m.active }), m.active ? "Account deactivated" : "Account reactivated");
  const changeRole = (m) => run(() => api.put(`/team/${m.id}`, { role: m.role === "admin" ? "member" : "admin" }), "Role updated");
  const del = (m) => { if (window.confirm(`Delete ${m.email}? They will lose access immediately.`)) run(() => api.delete(`/team/${m.id}`), "Account deleted"); };

  return (
    <div>
      <div className="flex items-end justify-between gap-6 mb-10 sv-rise">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-indigo-400 mb-3">Access</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Team</h1>
        </div>
        <button data-testid="team-add-button" onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-indigo-500 hover:bg-indigo-400 font-medium transition-colors">
          <Plus className="w-4 h-4" /> Add member
        </button>
      </div>

      {showAdd && (
        <form onSubmit={add} data-testid="team-add-form" className="rounded-lg border border-[#242C3D] bg-[#141A24]/80 p-6 mb-8 grid sm:grid-cols-[1.2fr_1fr_1fr_auto_auto] gap-3 items-end sv-rise">
          <div><label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Email *</label>
            <input data-testid="team-add-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="teammate@company.com" /></div>
          <div><label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Name *</label>
            <input data-testid="team-add-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Full name" /></div>
          <div><label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Password (8+ chars) *</label>
            <input data-testid="team-add-password-input" type="text" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} placeholder="Temporary password" /></div>
          <div><label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">Role</label>
            <select data-testid="team-add-role-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls}>
              <option value="member">Member</option><option value="admin">Admin</option>
            </select></div>
          <div className="flex gap-2">
            <button data-testid="team-add-submit-button" disabled={busy} className="h-10 px-5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-medium disabled:opacity-60">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add"}</button>
            <button type="button" onClick={() => setShowAdd(false)} className="h-10 px-4 rounded-full border border-[#242C3D] text-sm hover:bg-white/5">Cancel</button>
          </div>
        </form>
      )}

      {resetFor && (
        <form onSubmit={reset} data-testid="team-reset-form" className="rounded-lg border border-indigo-500/40 bg-indigo-500/5 p-6 mb-8 flex flex-wrap items-end gap-3 sv-rise">
          <div className="flex-1 min-w-56">
            <label className="block font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-2">New password for {resetFor.email} (8+ chars)</label>
            <input data-testid="team-reset-password-input" type="text" required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)} className={inputCls} />
          </div>
          <button data-testid="team-reset-submit-button" disabled={busy} className="h-10 px-5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-medium disabled:opacity-60">Set password</button>
          <button type="button" onClick={() => setResetFor(null)} className="h-10 px-4 rounded-full border border-[#242C3D] text-sm hover:bg-white/5">Cancel</button>
        </form>
      )}

      <div className="rounded-lg border border-[#242C3D] bg-[#141A24]/60 sv-rise sv-d1" data-testid="team-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left font-mono text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-6 py-3 font-medium">Member</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-6 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(members || []).map((m) => (
              <tr key={m.id} data-testid={`team-row-${m.email}`} className={`border-t border-[#242C3D] ${m.active ? "" : "opacity-50"}`}>
                <td className="px-6 py-4">
                  <div className="font-medium text-slate-100">{m.name || "—"}{m.id === me?.id && <span className="text-xs text-slate-500 ml-2">(you)</span>}</div>
                  <div className="font-mono text-xs text-slate-500">{m.email}</div>
                </td>
                <td className="px-4 py-4"><RoleBadge role={m.role} /></td>
                <td className="px-4 py-4"><span className={`text-xs ${m.active ? "text-emerald-400" : "text-slate-500"}`}>{m.active ? "Active" : "Deactivated"}</span></td>
                <td className="px-6 py-4">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`team-role-${m.email}`} onClick={() => changeRole(m)} className={`${mini} w-auto px-2 text-xs`} title="Change role">{m.role === "admin" ? "Make member" : "Make admin"}</button>
                    <button data-testid={`team-reset-${m.email}`} onClick={() => setResetFor(m)} className={mini} title="Reset password"><KeyRound className="w-4 h-4" /></button>
                    <button data-testid={`team-toggle-active-${m.email}`} onClick={() => toggleActive(m)} className={mini} title={m.active ? "Deactivate" : "Reactivate"}>
                      {m.active ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                    </button>
                    <button data-testid={`team-delete-${m.email}`} onClick={() => del(m)} className={`${mini} hover:!text-rose-400`} title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-slate-500 mt-4">Admins manage the team and everything else. Members can create and edit campaigns and view analytics, but cannot manage the team.</p>
    </div>
  );
}
